import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, ArrowLeft, Tag, Send } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Paywall from "../components/Paywall";
import ShareBar from "../components/ShareBar";
import ReferralNudge from "../components/ReferralNudge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentEmail, setCommentEmail] = useState("");
  const [commentName, setCommentName] = useState("");
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Premium check: verified via backend using locally stored subscriber email
  const [isPaidSubscriber, setIsPaidSubscriber] = useState(false);

  useEffect(() => {
    const subEmail = localStorage.getItem("fb_sub_email");
    if (subEmail) {
      axios.post(`${API}/premium/verify`, { email: subEmail })
        .then((r) => setIsPaidSubscriber(!!r.data.premium))
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        return axios.get(`${API}/posts/${slug}/comments`);
      })
      .then((r) => setComments(r.data || []))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !commentEmail) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/posts/${slug}/comments`, {
        email: commentEmail,
        name: commentName,
        content: commentText,
      });
      setComments((prev) => [...prev, res.data]);
      setCommentText("");
      toast.success("Comment posted!");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to post comment. Are you subscribed?");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Navbar />
        <p className="font-cormorant text-3xl text-foreground">Post not found</p>
        <Link to="/" className="font-manrope text-sm text-brand-accent hover:underline">Back to home</Link>
      </div>
    );
  }

  const isPaid = post.visibility === "paid";
  const showFull = !isPaid || isPaidSubscriber;
  const publishedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    : "";

  // If paid, only show first 3 paragraphs
  let displayContent = post.content;
  if (isPaid && !showFull) {
    const paragraphMatches = post.content.match(/(<p[^>]*>.*?<\/p>)/gs) || [];
    displayContent = paragraphMatches.slice(0, 3).join("") || post.content.substring(0, 600);
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Back link */}
          <Link
            to="/"
            data-testid="post-back-link"
            className="inline-flex items-center gap-1.5 font-manrope text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors duration-200"
          >
            <ArrowLeft size={15} />
            All articles
          </Link>

          {/* Post Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag) => (
                  <span key={tag} className="overline text-brand-accent">{tag}</span>
                ))}
              </div>
            )}
            <h1 className="font-cormorant text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-muted-foreground font-manrope text-sm">
              <span className="font-medium text-foreground">{post.author || "Elizabeth Neal"}</span>
              <span>·</span>
              <span>{publishedDate}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock size={13} />
                {post.reading_time || 3} min read
              </span>
              {isPaid && (
                <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent/10 text-brand-accent text-xs font-semibold">
                  Premium
                </span>
              )}
            </div>
            <div className="mt-5">
              <ShareBar title={post.title} idSuffix="-top" />
            </div>
          </motion.header>

          {/* Cover Image */}
          {post.cover_image && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="aspect-[16/9] rounded-xl overflow-hidden mb-10 shadow-sm"
            >
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
            </motion.div>
          )}

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div
              className="article-content text-foreground"
              data-testid="post-content"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />

            {/* Paywall */}
            {isPaid && !showFull && <Paywall onUnlock={() => setIsPaidSubscriber(true)} />}
          </motion.div>

          {/* Tags footer */}
          {showFull && post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-border font-manrope text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <Tag size={11} /> {tag}
                </span>
              ))}
            </div>
          )}

          {/* Share after reading */}
          {showFull && (
            <div className="mt-10 pt-6 border-t border-border">
              <p className="font-manrope text-sm text-muted-foreground mb-3">Enjoyed this? Share it with your network.</p>
              <ShareBar title={post.title} idSuffix="-bottom" />
              <ReferralNudge />
            </div>
          )}

          {/* Comments Section */}
          {showFull && (
            <section className="mt-14" data-testid="comments-section">
              <h2 className="font-cormorant text-2xl font-semibold text-foreground mb-6">
                Comments ({comments.length})
              </h2>

              {/* Comment list */}
              {comments.length === 0 ? (
                <p className="font-manrope text-sm text-muted-foreground mb-8">Be the first to comment. Subscribers only.</p>
              ) : (
                <div className="space-y-5 mb-8">
                  {comments.map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-cormorant text-sm font-semibold text-foreground shrink-0">
                        {(c.subscriber_name || "A")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-manrope text-sm font-semibold text-foreground">{c.subscriber_name || "Anonymous"}</p>
                        <p className="font-manrope text-xs text-muted-foreground mb-1">
                          {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                        <p className="font-manrope text-sm text-foreground leading-relaxed">{c.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Comment form */}
              <div className="border border-border rounded-xl p-5 bg-card">
                <h3 className="font-cormorant text-xl font-semibold text-foreground mb-4">Leave a comment</h3>
                <p className="font-manrope text-xs text-muted-foreground mb-4">Comments are for subscribers only. Please use your subscribed email.</p>
                <form onSubmit={handleComment} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Your name"
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      data-testid="comment-name-input"
                      className="px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                    />
                    <input
                      type="email"
                      placeholder="Subscribed email *"
                      value={commentEmail}
                      onChange={(e) => setCommentEmail(e.target.value)}
                      required
                      data-testid="comment-email-input"
                      className="px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                    />
                  </div>
                  <textarea
                    placeholder="Share your thoughts..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    required
                    rows={4}
                    data-testid="comment-text-input"
                    className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="comment-submit-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors duration-200"
                  >
                    <Send size={14} />
                    {submitting ? "Posting..." : "Post comment"}
                  </button>
                </form>
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
